import { inject, injectable } from 'inversify'
import type { Resolvable } from '@/services/resolvers/types'
import { SERVICE_TYPES } from '@/core/container/container'
import { withUniqueKeys } from '@/decorators/resolvers/withUniqueKeys'
import type { UniqueKeys } from '@/types'
import type UserPresenceService from '@/services/domain/user/userPresenceService'
import type UserService from '@/services/domain/user/userService'
import type { IMainServerSub, TServerEvent } from '@/services/transport/serverEvents'

@withUniqueKeys('UserPresenceResolver')
@injectable()
export default class UserPresenceResolver implements Resolvable {
  private eventHandler: (event: TServerEvent<'userPresenceStatusChanged'>) => void

  constructor(
    @inject(SERVICE_TYPES.UserPresenceService) protected readonly userPresenceService: UserPresenceService,
    @inject(SERVICE_TYPES.ServerSubscriptionService)
    protected readonly serverSubscriptionService: IMainServerSub,
    @inject(SERVICE_TYPES.UserService) protected readonly userService: UserService,
  ) {
    this.eventHandler = this.userPresenceStatusChangedHandler.bind(this)
  }

  public async resolve() {
    this.userPresenceService.fetchUserPresence()
    this.serverSubscriptionService.subscribe('userPresenceStatusChanged', this.eventHandler)
  }

  public async unresolve() {
    this.serverSubscriptionService.unsubscribe('userPresenceStatusChanged', this.eventHandler)
  }

  public getResolverKeys(): UniqueKeys {
    return []
  }

  protected userPresenceStatusChangedHandler(event: TServerEvent<'userPresenceStatusChanged'>) {
    const userId = String(event.payload.id)
    const presenceStatus = event.payload.presenceStatus

    this.userService.updateItemIfExists({
      id: userId,
      presenceStatus,
    })
    if (this.userService.isCurrentUser(userId)) {
      this.userPresenceService.storeUserPresence(presenceStatus)
    }
  }
}
